import React from 'react';
import PropTypes from 'prop-types';
import NextImage from 'next/image';
import capitalize from 'lodash/capitalize';
import { VisuallyHidden, Tooltip, PillText } from '@web/atoms';
import {
  Card,
  CardWrapper,
  Info,
  StyledImage,
  Name,
  Description,
  CardContent,
  CardHeader,
  MoreDetails,
  CheckboxContainer,
  Label,
  LabelText,
  ButtonWrapper,
  BadgeWrapper,
} from './styles.js';

export const RecommendedProductCard = ({
  label,
  title,
  badge,
  description,
  checked = false,
  img,
  onChange,
  onDetailsClick,
  showMoreDetails,
  unavailable,
  unavailableText = 'This recipe has been identified as an allergy concern',
  notSelectable,
  notSelectableText = 'To choose this item, please remove one of your previous selections.',
  level,
  orientation = 'row',
  offsetBadge = false,
  showSelectionButton = false,
  buttonIcon,
  width = 345,
}) => {
  let labelText = label;
  if (unavailable) {
    labelText = unavailableText || 'Recipe Unavailable';
  } else if (checked) {
    labelText = 'In your order';
  }

  const getProductCard = props => (
    <CardWrapper>
      {offsetBadge && badge && (
        <BadgeWrapper>
          <PillText text={badge} variant="dark" size="large" />
        </BadgeWrapper>
      )}
      <Card
        orientation={orientation}
        {...props}
        checked={checked}
        width={width}
        unavailable={unavailable}
        $notSelectable={notSelectable}
        aria-label={`${label} ${title}`}
        onClick={!notSelectable && onChange}
        showSelectionButton={showSelectionButton}
      >
        <CardContent
          orientation={orientation}
          showSelectionButton={showSelectionButton}
        >
          {orientation === 'column' && badge && (
            <PillText text={badge} variant="dark" size="small" />
          )}
          <StyledImage
            checked={checked}
            unavailable={unavailable}
            orientation={orientation}
          >
            <NextImage
              src={img.src}
              alt={img.alt}
              width={img.width ?? 82}
              height={img.height ?? 82}
            />
          </StyledImage>
          <Info checked={checked} orientation="column">
            <CardHeader>
              <Name as={level}>{title}</Name>
            </CardHeader>
            <Description orientation={orientation}>
              {unavailable ? unavailableText : description}
            </Description>
            {showMoreDetails && !unavailable && (
              <MoreDetails
                type="button"
                unavailable={unavailable}
                onClick={e => {
                  e.stopPropagation();
                  onDetailsClick();
                }}
                disabled={!showMoreDetails}
              >
                View Ingredients
              </MoreDetails>
            )}
          </Info>
          {showSelectionButton && (
            <ButtonWrapper>
              <CheckboxContainer
                checked={checked && !unavailable}
                unavailable={unavailable}
                icon={buttonIcon}
              >
                <Label
                  htmlFor={`${label}-checkbox-input`}
                  checked={checked && !unavailable}
                  unavailable={unavailable}
                  icon={buttonIcon}
                >
                  <VisuallyHidden>
                    {label + ' ' + capitalize(label)}
                  </VisuallyHidden>
                </Label>
                <LabelText
                  checked={checked}
                  unavailable={unavailable || notSelectable}
                >
                  {labelText}
                </LabelText>
              </CheckboxContainer>
            </ButtonWrapper>
          )}
        </CardContent>
      </Card>
    </CardWrapper>
  );

  return notSelectable && !unavailable ? (
    <Tooltip trigger={getProductCard} placement="top" offsetY={-200}>
      {notSelectableText}
    </Tooltip>
  ) : (
    getProductCard()
  );
};

RecommendedProductCard.displayName = 'RecommendedProductCard';
RecommendedProductCard.propTypes = {
  label: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  benefit: PropTypes.string,
  unavailable: PropTypes.bool,
  unavailableText: PropTypes.string,
  unavailableLabel: PropTypes.string,
  notSelectable: PropTypes.bool,
  notSelectableText: PropTypes.string,
  value: PropTypes.string.isRequired,
  description: PropTypes.node.isRequired,
  checked: PropTypes.bool,
  img: PropTypes.shape({
    src: PropTypes.string.isRequired,
    alt: PropTypes.string.isRequired,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  callout: PropTypes.string,
  showPriceText: PropTypes.bool,
  period: PropTypes.string,
  badge: PropTypes.string,
  level: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
  offsetBadge: PropTypes.bool,
  showSelectionButton: PropTypes.bool,
  buttonIcon: PropTypes.string,
  width: PropTypes.number,
};
